import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { BaseLineClean } from '@/app/components/icons/BaseLineClean';
import { BaseLineLeft } from '@/app/components/icons/BaseLineLeft';
import { Dialog, DialogContent } from '@/app/components/ui/dialog';
import { cn } from '@/app/utils';
import { EmptyState } from './components/EmptyState';
import { SingleMessageItem } from './components/SingleMessageItem';
import { SystemNotificationDetailDialog } from './components/SystemNotificationDetailDialog';
import { SystemNotificationItem } from './components/SystemNotificationItem';
import { useNotifications } from './hook/use-notifications';
import type { NotificationItem } from './types';

interface MobileNotificationsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const MobileNotifications = ({ open, onOpenChange }: MobileNotificationsProps) => {
  const t = useTranslations();
  const {
    handleMarkAllAsRead,
    handleClickSysNotification,
    openSysNotificationId,
    notifications,
    handleCloseDetail,
    hasMore,
    activeTab,
    changeActiveTab,
    getNotifications,
    setOpenSysNotificationId,
    initNotifications,
  } = useNotifications();

  useEffect(() => {
    if (open) {
      initNotifications();
    }
  }, [open]);

  const renderNotificationLists = () => {
    return (
      <div
        className="flex flex-1 flex-col gap-2 overflow-y-auto pt-2 sm:pb-[var(--ios-safe-bottom)]"
        id="scrollableContainer"
      >
        <InfiniteScroll
          dataLength={notifications.length}
          next={getNotifications}
          scrollableTarget="scrollableContainer"
          hasMore={hasMore}
          loader={<p className="text-center leading-[140px]">Loading...</p>}
          scrollThreshold={0.3}
          className="flex flex-col gap-2"
          endMessage={
            <div className="mt-8 text-center text-sm text-system-white64">{t('notifications.noMoreData')}</div>
          }
        >
          {notifications.map((notification: NotificationItem) => {
            const { type, system, interaction } = notification;
            if (type === 'system' && system) {
              return (
                <SystemNotificationItem
                  className="bg-system-white08"
                  data={system}
                  key={system?.notification?.id}
                  onNotificationClick={handleClickSysNotification}
                  setOpenSysNotificationId={setOpenSysNotificationId}
                />
              );
            }
            if (type === 'interaction' && interaction) {
              return (
                <SingleMessageItem
                  key={interaction.id}
                  data={interaction}
                  onNotificationClick={handleClickSysNotification}
                />
              );
            }
            return null;
          })}
        </InfiniteScroll>
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        if (!open) {
          onOpenChange(false);
        }
      }}
    >
      <DialogContent
        className="flex bg-system-bg08 px-0 py-0 text-system-white sm:pb-0"
        variant="fullScreen"
        closable={false}
      >
        <div className="flex flex-1 flex-col">
          <div className="flex h-11 items-center justify-center">
            <BaseLineLeft
              className="text-system-white text-xl"
              onClick={() => {
                onOpenChange(false);
              }}
            />
            <div className="flex flex-1 items-center justify-evenly font-semibold text-base text-system-white64">
              <div
                className={cn('relative', activeTab === 'interaction' && 'text-system-white')}
                onClick={() => changeActiveTab('interaction')}
              >
                {t('notifications.interactiveTitle')}
                {activeTab === 'interaction' && (
                  <div className="-bottom-1 absolute right-0 left-0 h-1 rounded-8 bg-ShengshuButton" />
                )}
              </div>
              <div
                className={cn('relative', activeTab === 'system' && 'text-system-white')}
                onClick={() => changeActiveTab('system')}
              >
                {t('notifications.platformTitle')}
                {activeTab === 'system' && (
                  <div className="-bottom-1 absolute right-0 left-0 h-1 rounded-8 bg-ShengshuButton" />
                )}
              </div>
            </div>
            <BaseLineClean className="size-5 cursor-pointer" onClick={handleMarkAllAsRead} />
          </div>
          {hasMore || notifications.length ? renderNotificationLists() : <EmptyState activeTab={activeTab} />}
        </div>
        {openSysNotificationId && (
          <SystemNotificationDetailDialog
            openSysNotificationId={openSysNotificationId}
            handleCloseDetail={handleCloseDetail}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
